import { Button } from "@/components/atoms/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  totalPages,
  currentPage,
  onPageChange,
}: PaginationProps) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex justify-center items-center gap-1 select-none">
      <Button
        size="sm"
        className="rounded-full bg-transparent hover:bg-muted text-muted-foreground transition-colors"
        disabled={currentPage === 1}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        aria-label="Anterior"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      {totalPages > 7 ? (
        <>
          <Button
            size="sm"
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors
              ${
                currentPage === 1
                  ? "bg-indigo-600 text-white hover:bg-indigo-700"
                  : "bg-transparent text-muted-foreground hover:bg-muted"
              }
            `}
            disabled={currentPage === 1}
            onClick={() => onPageChange(1)}
            aria-current={currentPage === 1 ? "page" : undefined}
          >
            1
          </Button>
          {currentPage > 3 && <span className="px-2">...</span>}
          {currentPage > 2 && (
            <Button
              size="sm"
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors bg-transparent text-muted-foreground hover:bg-muted`}
              onClick={() => onPageChange(currentPage - 1)}
            >
              {currentPage - 1}
            </Button>
          )}
          {currentPage !== 1 && currentPage !== totalPages && (
            <Button
              size="sm"
              className="rounded-full px-3 py-1 text-sm font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-600 cursor-default"
              disabled={true}
              aria-current="page"
            >
              {currentPage}
            </Button>
          )}
          {currentPage < totalPages - 1 && (
            <Button
              size="sm"
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors bg-transparent text-muted-foreground hover:bg-muted`}
              onClick={() => onPageChange(currentPage + 1)}
            >
              {currentPage + 1}
            </Button>
          )}
          {currentPage < totalPages - 2 && <span className="px-2">...</span>}
          <Button
            size="sm"
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors
              ${
                currentPage === totalPages
                  ? "bg-indigo-600 text-white hover:bg-indigo-700"
                  : "bg-transparent text-muted-foreground hover:bg-muted"
              }
            `}
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(totalPages)}
            aria-current={currentPage === totalPages ? "page" : undefined}
          >
            {totalPages}
          </Button>
        </>
      ) : (
        Array.from({ length: totalPages }, (_, i) => (
          <Button
            key={i + 1}
            size="sm"
            className={`rounded-full px-3 py-1 text-sm font-medium transition-colors
              ${
                currentPage === i + 1
                  ? "bg-indigo-600 text-white hover:bg-indigo-600 cursor-default"
                  : "bg-transparent text-muted-foreground hover:bg-muted"
              }
            `}
            disabled={currentPage === i + 1}
            onClick={() => currentPage !== i + 1 && onPageChange(i + 1)}
            aria-current={currentPage === i + 1 ? "page" : undefined}
          >
            {i + 1}
          </Button>
        ))
      )}
      <Button
        size="sm"
        className="rounded-full bg-transparent hover:bg-muted text-muted-foreground transition-colors"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        aria-label="Siguiente"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
}
